import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import api from '../../../api/axios';

const getFromTo = (dateRange, customStart, customEnd) => {
  const now = new Date();
  let from = new Date();
  let to = new Date();
  if (dateRange === '7d') {
    from.setDate(now.getDate() - 6);
  } else if (dateRange === '30d') {
    from.setDate(now.getDate() - 29);
  } else if (dateRange === 'custom' && customStart && customEnd) {
    from = new Date(customStart);
    to = new Date(customEnd);
  } else {
    return null;
  }
  from.setHours(0, 0, 0, 0);
  to.setHours(23, 59, 59, 999);
  const fmt = (d) => d.toISOString().split('.')[0];
  return { from: fmt(from), to: fmt(to) };
};

const colorSchemes = {
  promoters: {
    shades: ['#064e3b', '#065f46', '#047857', '#059669', '#10b981', '#34d399', '#6ee7b7', '#a7f3d0'],
    bg: 'bg-green-50',
    border: 'border-green-200',
    badge: 'bg-green-100 text-green-800',
    label: 'Promoters',
    icon: 'ThumbsUp',
  },
  passives: {
    shades: ['#78350f', '#92400e', '#b45309', '#d97706', '#f59e0b', '#fbbf24', '#fcd34d', '#fde68a'],
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    badge: 'bg-yellow-100 text-yellow-800',
    label: 'Passives',
    icon: 'Minus',
  },
  detractors: {
    shades: ['#7f1d1d', '#991b1b', '#b91c1c', '#dc2626', '#ef4444', '#f87171', '#fca5a5', '#fecaca'],
    bg: 'bg-red-50',
    border: 'border-red-200',
    badge: 'bg-red-100 text-red-800',
    label: 'Detractors',
    icon: 'ThumbsDown',
  },
};

const NPSWordCloud = () => {
  const [activeSegment, setActiveSegment] = useState('promoters');
  const [selectedWord, setSelectedWord] = useState(null);
  const [hoveredWord, setHoveredWord] = useState(null);
  const [questionFilter, setQuestionFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('30d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [appliedCustomRange, setAppliedCustomRange] = useState(null);
  const [words, setWords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dimensionOptions, setDimensionOptions] = useState([]);

  // Fetch dimensions for the question filter dropdown
  useEffect(() => {
    const range = getFromTo(dateFilter, customStart, customEnd);
    if (!range) return;
    api.get('/api/v1/nps-analytics/questions', { params: range })
      .then((res) => {
        const questions = res.data?.data?.questions || res.data?.questions || [];
        setDimensionOptions(questions.map((q) => q.dimension));
      })
      .catch(() => {});
  }, [dateFilter, customStart, customEnd]);

  // Fetch word cloud data
  useEffect(() => {
    const range = getFromTo(dateFilter, customStart, customEnd);
    if (!range) return;
    setLoading(true);
    const params = {
      ...range,
      segment: activeSegment,
    };
    if (questionFilter !== 'all') {
      params.dimension = questionFilter;
    }
    api.get('/api/v1/nps-analytics/themes', { params })
      .then((res) => {
        const data = res.data?.data || res.data;
        const mapped = (data?.words || []).map((w) => ({
          word: w.word,
          freq: w.frequency,
          sample: w.sampleQuote,
        }));
        setWords(mapped);
      })
      .catch((err) => console.error('Failed to fetch NPS themes:', err))
      .finally(() => setLoading(false));
  }, [dateFilter, customStart, customEnd, activeSegment, questionFilter]);

  const handleDateFilterChange = (val) => {
    setDateFilter(val);
    if (val !== 'custom') {
      setCustomStart('');
      setCustomEnd('');
      setAppliedCustomRange(null);
    }
  };

  const handleApplyCustomRange = () => {
    if (customStart && customEnd) {
      setAppliedCustomRange({ start: customStart, end: customEnd });
    }
  };

  const scheme = colorSchemes?.[activeSegment];
  const maxFreq = words.length > 0 ? Math.max(...words.map((w) => w?.freq)) : 1;
  const minFreq = words.length > 0 ? Math.min(...words.map((w) => w?.freq)) : 0;

  const getFontSize = (freq) => {
    if (maxFreq === minFreq) return 24;
    const normalized = (freq - minFreq) / (maxFreq - minFreq);
    return Math.round(12 + normalized * 28);
  };

  const getColor = (freq) => {
    if (maxFreq === minFreq) return scheme?.shades?.[4];
    const normalized = (freq - minFreq) / (maxFreq - minFreq);
    const idx = Math.floor(normalized * (scheme?.shades?.length - 1));
    return scheme?.shades?.[idx];
  };

  const segments = ['promoters', 'passives', 'detractors'];

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-foreground">Top Response Themes</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Voice of customer — hover to preview, click to explore responses</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {/* Date Filter */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
                {['7d', '30d', 'custom']?.map((d) => (
                  <button
                    key={d}
                    onClick={() => handleDateFilterChange(d)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                      dateFilter === d ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {d === '7d' ? 'Last 7 days' : d === '30d' ? 'Last 30 days' : 'Custom'}
                  </button>
                ))}
              </div>

              {/* Custom date inputs */}
              {dateFilter === 'custom' && (
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex items-center gap-1.5">
                    <label className="text-xs text-muted-foreground whitespace-nowrap">From:</label>
                    <input
                      type="date"
                      value={customStart}
                      max={customEnd || undefined}
                      onChange={(e) => setCustomStart(e?.target?.value)}
                      className="text-xs border border-border rounded-lg px-2 py-1.5 bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <label className="text-xs text-muted-foreground whitespace-nowrap">To:</label>
                    <input
                      type="date"
                      value={customEnd}
                      min={customStart || undefined}
                      onChange={(e) => setCustomEnd(e?.target?.value)}
                      className="text-xs border border-border rounded-lg px-2 py-1.5 bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                  <button
                    onClick={handleApplyCustomRange}
                    disabled={!customStart || !customEnd}
                    className="text-xs px-3 py-1.5 rounded-lg bg-primary text-primary-foreground font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-primary/90 transition-colors"
                  >
                    Apply
                  </button>
                  {appliedCustomRange && (
                    <span className="text-xs text-green-600 font-medium">✓ {appliedCustomRange?.start} → {appliedCustomRange?.end}</span>
                  )}
                </div>
              )}
            </div>

            {/* Question Selector */}
            <select
              value={questionFilter}
              onChange={(e) => setQuestionFilter(e?.target?.value)}
              className="text-xs border border-border rounded-lg px-3 py-2 bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="all">All Questions</option>
              {dimensionOptions?.map((dim) => (
                <option key={dim} value={dim}>{dim}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Segment Tabs */}
        <div className="flex items-center gap-2 mt-4">
          {segments?.map((seg) => {
            const s = colorSchemes?.[seg];
            return (
              <button
                key={seg}
                onClick={() => { setActiveSegment(seg); setSelectedWord(null); }}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all duration-200 border ${
                  activeSegment === seg
                    ? `${s?.badge} ${s?.border}`
                    : 'bg-muted text-muted-foreground border-transparent hover:bg-muted/80'
                }`}
              >
                <Icon name={s?.icon} size={12} />
                {s?.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="p-6">
        {/* Word Cloud */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : words.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">No theme data available for this segment and period.</div>
        ) : (
          <>
            <div className={`rounded-xl p-8 border ${scheme?.bg} ${scheme?.border} min-h-[280px] flex flex-wrap items-center justify-center gap-x-4 gap-y-3 mb-6`}>
              {words?.map((item) => (
                <div key={item?.word} className="relative group">
                  <button
                    className="transition-all duration-200 hover:scale-110 focus:outline-none"
                    style={{
                      fontSize: `${getFontSize(item?.freq)}px`,
                      color: getColor(item?.freq),
                      fontFamily: 'Outfit, sans-serif',
                      fontWeight: getFontSize(item?.freq) > 28 ? 700 : getFontSize(item?.freq) > 20 ? 600 : 500,
                      lineHeight: 1.3,
                      opacity: selectedWord && selectedWord?.word !== item?.word ? 0.4 : 1,
                    }}
                    onMouseEnter={() => setHoveredWord(item)}
                    onMouseLeave={() => setHoveredWord(null)}
                    onClick={() => setSelectedWord(selectedWord?.word === item?.word ? null : item)}
                  >
                    {item?.word}
                  </button>

                  {/* Hover tooltip */}
                  {hoveredWord?.word === item?.word && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 pointer-events-none">
                      <div className="bg-foreground text-background text-xs rounded-lg px-3 py-2 whitespace-nowrap shadow-lg">
                        <div className="font-semibold mb-0.5">{item?.freq} mentions</div>
                        <div className="opacity-80 max-w-[200px] truncate">"{item?.sample}"</div>
                        <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-foreground" />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Selected Word Detail Panel */}
            {selectedWord && (
              <div className={`rounded-xl border p-5 ${scheme?.bg} ${scheme?.border}`}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xl font-bold" style={{ color: getColor(selectedWord?.freq) }}>"{selectedWord?.word}"</span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${scheme?.badge}`}>{selectedWord?.freq} mentions</span>
                    </div>
                    <p className="text-xs text-muted-foreground">Responses containing this theme from {scheme?.label?.toLowerCase()}</p>
                  </div>
                  <button
                    onClick={() => setSelectedWord(null)}
                    className="w-7 h-7 rounded-lg bg-white/60 flex items-center justify-center hover:bg-white transition-colors"
                  >
                    <Icon name="X" size={14} color="#6B7280" />
                  </button>
                </div>

                <div className="space-y-3">
                  {/* Sample response */}
                  <div className="bg-white/70 rounded-lg p-4 border border-white/80">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="MessageSquare" size={14} color="#6B7280" />
                      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Sample Response</span>
                    </div>
                    <p className="text-sm text-foreground">"{selectedWord?.sample}"</p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Insight */}
        <div className="mt-4 flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-100">
          <Icon name="Lightbulb" size={14} color="#1E3A8A" />
          <p className="text-xs text-blue-800">
            {activeSegment === 'promoters' && <><span className="font-semibold">Insight:</span> Promoters frequently highlight positive themes — reinforce these in marketing messaging.</>}
            {activeSegment === 'passives' && <><span className="font-semibold">Insight:</span> Passives mention common concerns — potential conversion opportunity with targeted improvements.</>}
            {activeSegment === 'detractors' && <><span className="font-semibold">Insight:</span> Detractors frequently mention pain points — prioritize these in operational improvements.</>}
          </p>
        </div>
      </div>
    </div>
  );
};

export default NPSWordCloud;
